/**
 * 5 CLASS NHÂN VẬT DEFEND + 7 SKILL mỗi class.
 * Toàn bộ là DỮ LIỆU ⇒ sửa số liệu không cần build lại code.
 *
 * TÔNG TÊN CHIÊU (người dùng chốt từng class):
 *  · Dân tập gym — hài kiểu phòng tập (tạ · whey · set cuối).
 *  · Gái ngành (id `agility`, tên EN vẫn là "Baby three") — THIẾU NỮ ĐẸP, ngọt ngào quyến rũ
 *    bề mặt nhưng nham hiểm chết chóc bên trong: khiêu vũ · ghé sát tai · tặng hoa · hôn gió ·
 *    lụa bay (KHÔNG dùng hình ảnh trẻ con).
 *  · Dân chợ búa — dép · rau củ · nước mắm · thị phi.
 *  · Bắn thuê — lính đánh thuê: xả đạn · lựu đạn chợ đen · gọi pháo.
 *  · Đẻ mướn — nhận đơn là đẻ: đẻ sỉ · hợp đồng dài hạn · đẻ công nghiệp.
 */
import type { ClassId, Stats } from '../sim/types';
import type { CastAnimId } from './skill-anims';

export type { ClassId, Stats };

export type SkillKind =
  | 'aoeDamage'
  | 'projectile'
  | 'heal'
  | 'buff'
  | 'debuff'
  | 'summon'
  | 'invuln'
  | 'shield'
  | 'dashStrike';

export interface SkillDef {
  id: string;
  name: string;
  desc: string;
  kind: SkillKind;
  manaCost: number;
  cooldown: number;
  /** mở khoá ở wave nào */
  unlockWave: number;
  mult?: number;
  radius?: number;
  maxTargets?: number;
  count?: number;
  spread?: number;
  projectileSpeed?: number;
  amount?: number;
  pct?: number;
  duration?: number;
  distance?: number;
  stat?: keyof Stats;
  buffMult?: number;
  summonKind?: string;
  /**
   * TẦM ĐỂ AUTO DÙNG CHIÊU (px) — chỉ dùng cho bot tự niệm.
   *
   * Vì sao cần khai riêng: chiêu ném/ bắn có tầm khác hẳn tầm đánh thường (Lựu Đạn ném
   * xa hơn súng, Pháo Binh còn xa hơn). Không khai thì `World.skillReach()` lấy
   * `max(tầm đánh, 320)`; với chiêu nổ quanh người thì lấy `radius`, chiêu lao tới lấy
   * `distance` — nên phần lớn chiêu KHÔNG cần khai.
   */
  range?: number;
  /**
   * DÁNG RA CHIÊU — khớp với tên chiêu. Xem data/skill-anims.ts.
   * Không khai báo thì lấy theo `kind` (xem animOfSkill).
   */
  anim?: CastAnimId;
}

export interface ClassDef {
  id: ClassId;
  name: string;
  title: string;
  role: string;
  blurb: string;
  /** màu nhận diện (dùng cho UI + màu trang phục mặc định) */
  color: number;
  accent: number;
  base: Stats;
  growth: Partial<Stats>; // mỗi cấp phòng tập luyện
  trainingBaseCost: number;
  /** quân đội phải mua đạn */
  usesAmmo?: boolean;
  /**
   * ĐÁNH XA — LUẬT "CLASS NÀO NÉM/ BẮN" NẰM Ở DỮ LIỆU, KHÔNG RẢI TRONG CODE.
   *
   * ⚠️⚠️ VÌ SAO LÀ MỘT TRƯỜNG DỮ LIỆU CHỨ KHÔNG PHẢI `if (cls.id === 'army' || …)` Ở HAI CHỖ:
   * luật này từng bị CHÉP ở hai nơi — `World.heroAttack` (chế độ đi ải) và `ArenaWorld.basicShotSkin`
   * (Arena). Thêm một class đánh xa là phải sửa 2 chỗ; quên 1 chỗ thì hai chế độ lệch nhau đúng kiểu
   * "người chơi thấy đánh xa ở màn này mà cận chiến ở màn kia". Nay CẢ HAI đọc đúng trường này.
   * `kind` = đại loại vật lý (`bullet` đạn súng · `thrown` đồ ném), `skin` = tên hình vẽ (xem bảng
   * `SHAPES` trong `render/projectiles.ts`). Đòn CẬN CHIẾN thì KHÔNG khai trường này.
   */
  ranged?: { kind: 'bullet' | 'thrown'; skin: string };
  /** mẹ thiên hạ đẻ quân mỗi N giây */
  summonEvery?: number;
  summonKind?: string;
  skills: SkillDef[];
}

export const CLASSES: Record<ClassId, ClassDef> = {
  strength: {
    id: 'strength',
    name: 'Dân tập gym',
    title: 'Cơ Bắp',
    role: 'Tank / cận chiến nặng',
    blurb: 'Thân hình như quả tạ. Đứng chặn cửa, wave đầu chấp cả bầy mà không mất máu.',
    color: 0xd97706,
    accent: 0xfbbf24,
    base: {
      hp: 1900, atk: 134, def: 108, mana: 100, stamina: 100,
      atkSpeed: 0.95, range: 66, moveSpeed: 172, dodge: 0.05, crit: 0.1,
      manaRegen: 4, staminaRegen: 6,
    },
    growth: { hp: 190, atk: 13, def: 11, stamina: 6 },
    trainingBaseCost: 120,
    skills: [
      { id: 'ironSkin', name: 'Gồng Cơ Khoe Bụng', desc: 'Siết cơ giảm 40% sát thương nhận vào trong 6s', kind: 'buff', manaCost: 30, cooldown: 16, unlockWave: 1, stat: 'def', buffMult: 2.2, duration: 6, anim: 'meditate' },
      { id: 'powerFist', name: 'Đấm Tạ 100kg', desc: 'Quật tạ vỡ sàn: 260% sát thương quanh người', kind: 'aoeDamage', manaCost: 35, cooldown: 8, unlockWave: 2, mult: 2.6, radius: 110, maxTargets: 6, anim: 'doubleFist' },
      { id: 'taunt', name: 'Khoe Bắp Tay Kéo Quái', desc: 'Khoe cơ khiến mọi quái trong 260px lao vào mình 5s', kind: 'debuff', manaCost: 25, cooldown: 14, unlockWave: 4, stat: 'atkSpeed', buffMult: 0.7, duration: 5, radius: 260, maxTargets: 99, anim: 'taunt' },
      { id: 'quake', name: 'Dậm Sàn Tầng G+', desc: 'Dậm sàn rung cả phòng tập: 180% sát thương + làm chậm quanh người', kind: 'aoeDamage', manaCost: 45, cooldown: 12, unlockWave: 6, mult: 1.8, radius: 150, maxTargets: 10, anim: 'stomp' },
      { id: 'bulwark', name: 'Whey Protein Khiên', desc: 'Nốc whey: khiên chắn 45% máu tối đa trong 8s', kind: 'shield', manaCost: 40, cooldown: 18, unlockWave: 8, pct: 0.45, duration: 8, anim: 'guard' },
      { id: 'unstoppable', name: 'Quá Tải Cơ Bắp', desc: 'Vào set cuối: bất tử 4s', kind: 'invuln', manaCost: 60, cooldown: 30, unlockWave: 9, duration: 4, anim: 'powerUp' },
      { id: 'dragonStrike', name: 'Tuyệt Kỹ: Đại Lực Vô Song', desc: 'Tuyệt chiêu: 700% sát thương diện rộng', kind: 'aoeDamage', manaCost: 90, cooldown: 40, unlockWave: 10, mult: 7, radius: 220, maxTargets: 99, anim: 'palmPush' },
    ],
  },

  agility: {
    id: 'agility',
    /*
     * TÊN TIẾNG VIỆT — người dùng chốt *"Tiếng Việt đổi Baby three thành Gái ngành"*.
     *
     * ⚠️ Bản TIẾNG ANH cố ý KHÁC: người dùng chốt *"Gái ngành Tiếng Anh để là `Baby three` đi"*
     * ⇒ `CLASS_EN['agility'].name = 'Baby three'` (xem `i18n/content-en.ts`).
     * **KHÔNG "sửa cho khớp" hai bên** — hai ngôn ngữ gọi tên khác nhau là CHỦ Ý của người dùng,
     * không phải lỗi cần đồng bộ.
     *
     * ⚠️⚠️ `id: 'agility'` là KHOÁ KỸ THUẬT — TUYỆT ĐỐI không đổi theo tên hiển thị. Đổi id làm
     * hỏng save cũ (`fromClass` trong localStorage), `art/ai/agility-parts.png`,
     * `art/skills/agility/`, và mọi tham chiếu `ClassId` trong `sim/`.
     */
    name: 'Gái ngành',
    /*
     * ⚠️ TÔNG: người dùng chốt *"Baby three không phải em bé mà là 1 người con gái đẹp"* — KHÔNG
     * dùng hình ảnh trẻ con (bỉm/ sữa/ nhà trẻ/ vỗ tay). Đây là THIẾU NỮ ĐẸP: bề mặt ngọt ngào
     * quyến rũ (khiêu vũ · ghé sát tai · tặng hoa · hôn gió · lụa bay) nhưng bên trong là sát ý
     * chết chóc — mô tả của từng chiêu mới lộ ra sát thương.
     * (Tên cũ "Baby three" trong câu trích chỉ là tên CŨ; tông thiết kế KHÔNG đổi theo tên.)
     */
    title: 'Hồng Nhan',
    role: 'Sát thủ quyến rũ / né cao',
    blurb: 'Nụ cười đẹp đến mức không ai kịp nhớ phải chạy. Máu mỏng, nhưng chưa ai chạm được vào cô ấy.',
    color: 0x0ea5e9,
    accent: 0x67e8f9,
    base: {
      hp: 950, atk: 70, def: 38, mana: 120, stamina: 150,
      atkSpeed: 2.2, range: 58, moveSpeed: 265, dodge: 0.3, crit: 0.25,
      manaRegen: 6, staminaRegen: 14,
    },
    growth: { hp: 95, atk: 8, def: 4, stamina: 10, dodge: 0.002 },
    trainingBaseCost: 120,
    skills: [
      { id: 'flurry', name: 'Khiêu Vũ Bốn Nhịp', desc: 'Khiêu vũ bốn nhịp — mỗi nhịp là một cú đá: tổng 240% sát thương', kind: 'aoeDamage', manaCost: 25, cooldown: 7, unlockWave: 1, mult: 2.4, radius: 80, maxTargets: 4, anim: 'kickCombo' },
      { id: 'shadowStep', name: 'Ghé Sát Tai', desc: 'Ghé sát tai thì thầm rồi lướt tới: lao 220px, 200% sát thương', kind: 'dashStrike', manaCost: 25, cooldown: 9, unlockWave: 2, distance: 220, mult: 2, anim: 'dash' },
      { id: 'smoke', name: 'Núp Sau Tóc', desc: 'Vén tóc che mặt: +45% né tránh trong 6s', kind: 'buff', manaCost: 30, cooldown: 15, unlockWave: 4, stat: 'dodge', buffMult: 1.45, duration: 6, anim: 'crouch' },
      { id: 'shuriken', name: 'Tặng Năm Đoá Hoa', desc: 'Tặng 5 đoá hoa — mỗi đoá 90% sát thương', kind: 'projectile', manaCost: 30, cooldown: 6, unlockWave: 6, mult: 0.9, count: 5, spread: 0.5, projectileSpeed: 620, anim: 'volley' },
      { id: 'afterimage', name: 'Hôn Gió', desc: 'Hôn gió 2,5s: không ai chạm được, ai chạm bị phản 150% sát thương', kind: 'invuln', manaCost: 45, cooldown: 24, unlockWave: 8, duration: 2.5, anim: 'powerUp' },
      { id: 'haste', name: 'Sải Bước Miêu', desc: 'Sải bước trên sàn: +70% tốc đánh và tốc chạy trong 7s', kind: 'buff', manaCost: 40, cooldown: 20, unlockWave: 9, stat: 'atkSpeed', buffMult: 1.7, duration: 7, anim: 'powerUp' },
      { id: 'thousandHands', name: 'Tuyệt Kỹ: Vũ Khúc Hồng Nhan', desc: 'Tuyệt chiêu: vũ khúc 12 nhịp — mỗi nhịp 130% sát thương', kind: 'aoeDamage', manaCost: 85, cooldown: 36, unlockWave: 10, mult: 1.3, radius: 190, maxTargets: 99, anim: 'punchCombo' },
    ],
  },

  street: {
    id: 'street',
    name: 'Dân chợ búa',
    title: 'Buôn Chuyện',
    role: 'Khống chế / ném đồ diện rộng',
    blurb: 'Miệng lưỡi chợ búa hạ nhục quái, dép ném trúng cả đám.',
    color: 0x16a34a,
    accent: 0x86efac,
    base: {
      hp: 1100, atk: 66, def: 46, mana: 150, stamina: 95,
      atkSpeed: 1.1, range: 195, moveSpeed: 165, dodge: 0.1, crit: 0.12,
      manaRegen: 9, staminaRegen: 6,
    },
    growth: { hp: 110, atk: 7, def: 5, mana: 12 },
    trainingBaseCost: 110,
    ranged: { kind: 'thrown', skin: 'street-basic' }, // ném dép
    skills: [
      { id: 'trashTalk', name: 'Đả Kích Tinh Thần', desc: 'Giảm 35% sát thương quái trong 240px, 6s', kind: 'debuff', manaCost: 25, cooldown: 10, unlockWave: 1, stat: 'atk', buffMult: 0.65, duration: 6, radius: 240, maxTargets: 99, anim: 'shout' },
      { id: 'sandalThrow', name: 'Ném Dép', desc: 'Ném dép trúng 4 địch, 120% mỗi địch', kind: 'projectile', manaCost: 30, cooldown: 7, unlockWave: 2, mult: 1.2, count: 4, spread: 0.9, projectileSpeed: 520, anim: 'hurl' },
      { id: 'marketRumor', name: 'Tin Đồn Chợ', desc: 'Làm chậm 45% tốc chạy quái 7s', kind: 'debuff', manaCost: 30, cooldown: 14, unlockWave: 4, stat: 'moveSpeed', buffMult: 0.55, duration: 7, radius: 280, maxTargets: 99, anim: 'shout' },
      { id: 'veggieBarrage', name: 'Mưa Rau Củ', desc: 'Ném 8 món, mỗi món 85% sát thương', kind: 'projectile', manaCost: 40, cooldown: 9, unlockWave: 6, mult: 0.85, count: 8, spread: 1.2, projectileSpeed: 480, anim: 'fanSweep' },
      { id: 'fishSauce', name: 'Nước Mắm Độc', desc: 'AoE 200% + giảm 50% giáp quái 8s', kind: 'aoeDamage', manaCost: 45, cooldown: 16, unlockWave: 8, mult: 2, radius: 200, maxTargets: 12, anim: 'hurl' },
      { id: 'gossipShield', name: 'Lá Chắn Thị Phi', desc: 'Khiên 35% máu trong 8s', kind: 'shield', manaCost: 35, cooldown: 20, unlockWave: 9, pct: 0.35, duration: 8, anim: 'guard' },
      { id: 'marketChaos', name: 'Chợ Vỡ', desc: 'Tuyệt chiêu: 550% diện rộng + choáng 4s', kind: 'aoeDamage', manaCost: 90, cooldown: 38, unlockWave: 10, mult: 5.5, radius: 300, maxTargets: 99, anim: 'groundSlam' },
    ],
  },

  army: {
    id: 'army',
    name: 'Bắn thuê',
    title: 'Lính Đánh Thuê',
    role: 'Xạ thủ tầm xa',
    blurb: 'Trả tiền là bắn, hết tiền là nghỉ. Sát thương cao nhưng phải mua đạn bằng vàng.',
    color: 0x4f46e5,
    accent: 0xa5b4fc,
    base: {
      hp: 1000, atk: 96, def: 56, mana: 110, stamina: 80,
      atkSpeed: 1.4, range: 265, moveSpeed: 150, dodge: 0.08, crit: 0.18,
      manaRegen: 5, staminaRegen: 5,
    },
    growth: { hp: 100, atk: 11, def: 6 },
    trainingBaseCost: 140,
    usesAmmo: true,
    ranged: { kind: 'bullet', skin: 'army-basic' }, // đạn súng
    skills: [
      { id: 'suppressive', name: 'Xả Đạn Chế Áp', desc: 'Bắn liên thanh 6 viên, mỗi viên 95%', kind: 'projectile', manaCost: 30, cooldown: 8, unlockWave: 1, mult: 0.95, count: 6, spread: 0.35, projectileSpeed: 780, anim: 'volley' },
      { id: 'grenade', name: 'Lựu Đạn Chợ Đen', desc: 'AoE 260% trong 130px', kind: 'aoeDamage', manaCost: 35, cooldown: 10, unlockWave: 2, mult: 2.6, radius: 130, maxTargets: 8, anim: 'hurl' },
      { id: 'armorPiercing', name: 'Đạn Xuyên Giáp', desc: 'Bỏ qua 100% giáp địch ở đòn kế tiếp', kind: 'buff', manaCost: 25, cooldown: 12, unlockWave: 4, stat: 'atk', buffMult: 1.5, duration: 5, anim: 'spearHand' },
      { id: 'artillery', name: 'Gọi Pháo Thuê', desc: '3 quả pháo, mỗi quả 170%', kind: 'projectile', manaCost: 45, cooldown: 14, unlockWave: 6, mult: 1.7, count: 3, spread: 0.6, projectileSpeed: 460, anim: 'hurl' },
      { id: 'entrench', name: 'Đào Công Sự', desc: '+60% giáp, +25% tốc đánh trong 8s', kind: 'buff', manaCost: 35, cooldown: 18, unlockWave: 8, stat: 'def', buffMult: 1.6, duration: 8, anim: 'guard' },
      { id: 'ammoDrop', name: 'Tiếp Vận Đạn', desc: 'Nhận ngay 40 viên đạn', kind: 'heal', manaCost: 20, cooldown: 25, unlockWave: 9, amount: 40, anim: 'reload' },
      { id: 'finalSalvo', name: 'Tuyệt Kỹ: Bắn Sạch Băng', desc: 'Tuyệt chiêu: 16 viên xuyên phá, mỗi viên 120%', kind: 'projectile', manaCost: 95, cooldown: 40, unlockWave: 10, mult: 1.2, count: 16, spread: 1.4, projectileSpeed: 820, anim: 'volley' },
    ],
  },

  mother: {
    id: 'mother',
    name: 'Đẻ mướn',
    title: 'Triệu Hồi',
    role: 'Triệu hồi / ném trứng tầm xa',
    blurb: 'Nhận đơn là đẻ, rảnh tay thì ném trứng gà. Chỉ số thấp, nhưng 10 giây một đứa — đông thì thắng.',
    color: 0xdb2777,
    accent: 0xf9a8d4,
    base: {
      hp: 1300, atk: 50, def: 62, mana: 130, stamina: 100,
      /*
       * TẦM ĐÁNH 95 → 200: người dùng yêu cầu *"Đổi class Đẻ mướn ở cả đi ải và arena thành đánh
       * tầm xa… đường đạn là ném quả trứng gà"*. Tầm 95 gần như cận chiến nên đạn bay chỉ một quãng
       * ngắn rồi trúng — đúng lỗi "một cục loé lên rồi biến mất" đã ghi ở `spawnProjectile`. 200 nằm
       * giữa street (195) và army (265) nên vẫn dưới tầm xa nhất (`tests/sim.test.ts` khoá army là
       * class có tầm xa nhất) nhưng đủ để thấy đường bay.
       */
      atkSpeed: 0.9, range: 200, moveSpeed: 160, dodge: 0.06, crit: 0.08,
      manaRegen: 8, staminaRegen: 8,
    },
    growth: { hp: 130, atk: 6, def: 7, mana: 10 },
    trainingBaseCost: 130,
    ranged: { kind: 'thrown', skin: 'mother-basic' }, // ném quả trứng gà
    /*
     * NHỊP ĐẺ CỦA "MẸ THIÊN HẠ": 10 giây/ 1 con (người dùng yêu cầu, trước là 5).
     * Điều kiện "CHỈ ĐẺ KHI ĐANG CHIẾN ĐẤU" nằm ở `World.updateHero()` — xem chú thích ở đó.
     */
    summonEvery: 10,
    summonKind: 'brat',
    skills: [
      /*
       * ⚠️ TÊN CHIÊU LÀ "Sinh Ngay MỘT Đứa" NHƯNG `count` ĐANG LÀ 3 — code mâu thuẫn với chính
       * tên của nó. Người dùng: "skill mẹ thiên hạ sinh ngay 1 đứa hiện đang sinh 3, sửa thành
       * sinh 1 đứa thôi". Nay `count: 1` và mô tả khớp theo.
       * (Các chiêu đẻ nhiều con khác vẫn giữ: `hordeCall` 6 con, `grandMother` 12 con.)
       */
      { id: 'giveBirth', name: 'Nhận Đơn Đẻ Ngay', desc: 'Đẻ ngay 1 đứa con', kind: 'summon', manaCost: 30, cooldown: 10, unlockWave: 1, count: 1, summonKind: 'brat', duration: 40, anim: 'summon' },
      { id: 'scold', name: 'La Mắng Vang Xóm', desc: 'AoE 150% + giảm 30% tốc đánh quái 5s', kind: 'aoeDamage', manaCost: 28, cooldown: 9, unlockWave: 2, mult: 1.5, radius: 170, maxTargets: 10, anim: 'shout' },
      { id: 'favoritism', name: 'Bồi Dưỡng Tại Nhà', desc: '+50% máu và giáp cho cả đội trong 8s', kind: 'buff', manaCost: 40, cooldown: 18, unlockWave: 4, stat: 'def', buffMult: 1.5, duration: 8, anim: 'powerUp' },
      { id: 'hordeCall', name: 'Đẻ Sỉ', desc: 'Đẻ 6 đứa con', kind: 'summon', manaCost: 55, cooldown: 20, unlockWave: 6, count: 6, summonKind: 'brat', duration: 45, anim: 'summon' },
      { id: 'motherWrath', name: 'Vỡ Đơn', desc: 'AoE 320% quanh người', kind: 'aoeDamage', manaCost: 50, cooldown: 15, unlockWave: 8, mult: 3.2, radius: 210, maxTargets: 99, anim: 'groundSlam' },
      { id: 'endlessLine', name: 'Hợp Đồng Dài Hạn', desc: '10s mỗi 2s đẻ 1 đứa', kind: 'summon', manaCost: 65, cooldown: 30, unlockWave: 9, count: 5, summonKind: 'brat', duration: 40, anim: 'summon' },
      { id: 'grandMother', name: 'Tuyệt Kỹ: Đẻ Công Nghiệp', desc: 'Tuyệt chiêu: đẻ 12 đứa tinh nhuệ', kind: 'summon', manaCost: 95, cooldown: 45, unlockWave: 10, count: 12, summonKind: 'eliteBrat', duration: 60, anim: 'summon' },
    ],
  },

  /*
   * ============================================================================================
   * TODO(design) — 2 CLASS DƯỚI ĐÂY LÀ KHUNG PLACEHOLDER, CHƯA CÓ THIẾT KẾ THẬT.
   *
   * Người dùng chốt: *"Tạo sẵn cho tôi 2 class trong shop, class Thợ mộc, class Thợ lặn
   * (design, skill, mọi thứ mình sẽ làm sau)"*.
   *
   * ⇒ TÊN CLASS, TÊN CHIÊU, MÔ TẢ, MỌI CON SỐ (base/ growth/ trainingBaseCost/ manaCost/
   *   cooldown/ mult/ radius/ count/ duration…) ĐỀU LÀ TẠM. Chúng được copy TINH THẦN từ class
   *   cùng vai trò đang có (khung xác → tank cận chiến; thợ lặn → sát thủ/ đánh xa) để dữ liệu
   *   HỢP LỆ và chơi được, KHÔNG nhằm mục đích cân bằng. Sẽ thiết kế lại toàn bộ sau.
   * ⇒ 2 class này CHƯA MỞ KHOÁ nên KHÔNG nằm trong `CLASS_ORDER` (xem cuối file) — vẫn phải có
   *   mặt trong `CLASSES` vì `Record<ClassId, ClassDef>` đòi đủ khoá.
   * ============================================================================================
   */

  // TODO(design): id/ tên/ số liệu TẠM — xem khối chú thích ngay trên. Vai trò: tank cận chiến.
  carpenter: {
    id: 'carpenter',
    name: 'Thợ mộc',
    title: 'Búa Gỗ',
    role: 'Cận chiến / dựng chướng ngại',
    blurb: 'Vác cả xưởng gỗ ra trận. Tay búa nện như đóng cọc, chỉ số tạm copy tinh thần Dân tập gym.',
    color: 0x92400e,
    accent: 0xfcd34d,
    base: {
      hp: 1700, atk: 120, def: 96, mana: 100, stamina: 105,
      atkSpeed: 0.95, range: 68, moveSpeed: 170, dodge: 0.05, crit: 0.1,
      manaRegen: 4, staminaRegen: 6,
    },
    growth: { hp: 170, atk: 12, def: 10, stamina: 6 },
    trainingBaseCost: 120,
    skills: [
      { id: 'hammerSmash', name: 'Búa Gỗ', desc: 'Nện búa xuống sàn: 240% sát thương quanh người', kind: 'aoeDamage', manaCost: 30, cooldown: 8, unlockWave: 1, mult: 2.4, radius: 105, maxTargets: 6, anim: 'doubleFist' },
      { id: 'nailDrive', name: 'Đóng Đinh', desc: 'Đóng đinh xuyên tới: 130% sát thương', kind: 'projectile', manaCost: 25, cooldown: 7, unlockWave: 2, mult: 1.3, count: 3, spread: 0.4, projectileSpeed: 540, anim: 'spearHand' },
      { id: 'sawSplit', name: 'Cưa Đôi', desc: 'Cưa đôi địch: 200% sát thương trong 130px', kind: 'aoeDamage', manaCost: 35, cooldown: 11, unlockWave: 4, mult: 2, radius: 130, maxTargets: 8, anim: 'spin' },
      { id: 'woodWard', name: 'Ván Chắn Gỗ', desc: 'Dựng ván chắn: +55% giáp trong 8s', kind: 'buff', manaCost: 35, cooldown: 18, unlockWave: 6, stat: 'def', buffMult: 1.55, duration: 8, anim: 'guard' },
      { id: 'sawdust', name: 'Mùn Cưa Bay', desc: 'Tung mùn cưa mù mắt: giảm 45% tốc chạy trong 6s', kind: 'debuff', manaCost: 30, cooldown: 14, unlockWave: 7, stat: 'moveSpeed', buffMult: 0.55, duration: 6, radius: 230, maxTargets: 99, anim: 'fanSweep' },
      { id: 'woodenShield', name: 'Khiên Gỗ Lim', desc: 'Khiên gỗ lim: chắn 40% máu tối đa trong 8s', kind: 'shield', manaCost: 40, cooldown: 20, unlockWave: 9, pct: 0.4, duration: 8, anim: 'guard' },
      { id: 'ultimateSaw', name: 'Tuyệt Kỹ: Xưởng Gỗ Nổ', desc: 'Tuyệt chiêu: 580% sát thương diện rộng', kind: 'aoeDamage', manaCost: 90, cooldown: 40, unlockWave: 10, mult: 5.8, radius: 230, maxTargets: 99, anim: 'groundSlam' },
    ],
  },

  // TODO(design): id/ tên/ số liệu TẠM — xem khối chú thích ngay trên. Vai trò: sát thủ/ đánh xa.
  diver: {
    id: 'diver',
    name: 'Thợ lặn',
    title: 'Ống Thở',
    role: 'Đánh xa / luồn sâu',
    blurb: 'Lặn xuống rồi trồi lên sau lưng quái. Chỉ số tạm copy tinh thần sát thủ né cao.',
    color: 0x0891b2,
    accent: 0x67e8f9,
    base: {
      hp: 1050, atk: 88, def: 44, mana: 130, stamina: 135,
      atkSpeed: 1.6, range: 210, moveSpeed: 200, dodge: 0.22, crit: 0.2,
      manaRegen: 7, staminaRegen: 11,
    },
    growth: { hp: 105, atk: 9, def: 5, mana: 10, stamina: 8 },
    trainingBaseCost: 130,
    skills: [
      { id: 'airTank', name: 'Bình Khí', desc: 'Nạp bình khí: +55% tốc đánh trong 7s', kind: 'buff', manaCost: 30, cooldown: 14, unlockWave: 1, stat: 'atkSpeed', buffMult: 1.55, duration: 7, anim: 'powerUp' },
      { id: 'spearGun', name: 'Súng Xiên Cá', desc: 'Bắn xiên cá: 140% sát thương', kind: 'projectile', manaCost: 25, cooldown: 7, unlockWave: 2, mult: 1.4, count: 4, spread: 0.5, projectileSpeed: 600, anim: 'volley' },
      { id: 'deepDive', name: 'Lặn Sâu', desc: 'Lặn xuống rồi trồi lên: lao 230px, 210% sát thương', kind: 'dashStrike', manaCost: 30, cooldown: 10, unlockWave: 4, distance: 230, mult: 2.1, anim: 'dash' },
      { id: 'harpoonNet', name: 'Lưới Lao', desc: 'Quăng lưới lao: giảm 50% tốc chạy trong 6s', kind: 'debuff', manaCost: 30, cooldown: 15, unlockWave: 6, stat: 'moveSpeed', buffMult: 0.5, duration: 6, radius: 250, maxTargets: 99, anim: 'hurl' },
      { id: 'breathHold', name: 'Nín Thở', desc: 'Nín thở 3s: bất tử, ai chạm bị phản 160%', kind: 'invuln', manaCost: 45, cooldown: 26, unlockWave: 8, duration: 3, anim: 'meditate' },
      { id: 'flipperKick', name: 'Chân Vịt', desc: 'Đá chân vịt: 190% sát thương quanh người', kind: 'aoeDamage', manaCost: 35, cooldown: 12, unlockWave: 9, mult: 1.9, radius: 115, maxTargets: 6, anim: 'spin' },
      { id: 'ultimateDepth', name: 'Tuyệt Kỹ: Đáy Biển Sâu', desc: 'Tuyệt chiêu: 520% sát thương diện rộng', kind: 'aoeDamage', manaCost: 90, cooldown: 38, unlockWave: 10, mult: 5.2, radius: 210, maxTargets: 99, anim: 'palmPush' },
    ],
  },
};

export const CLASS_ORDER: ClassId[] = ['strength', 'agility', 'street', 'army', 'mother'];
